import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Progress } from '../ui/progress'
import { Badge } from '../ui/badge'
import { ConfirmationDialog } from '../ui/confirmation-dialog'
import {
    AlertTriangle,
    Ban,
    ImageDown,
    Loader2,
    Play,
    RefreshCw,
    Wrench,
} from 'lucide-react'
import { toast } from 'sonner'
import { DateTime } from 'luxon'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import MaintenanceEndpoints from '@/endpoints/MaintenanceEndpoints'
import { ImageDownscaleState, ImageDownscaleStatus } from '@/model/Maintenance'
import { useSessionStore } from '@/stores/useSessionStore'

const MIN_DIMENSION = 200
const MAX_DIMENSION = 4000
const DEFAULT_DIMENSION = 1000
/** How often to re-poll while a job is RUNNING. */
const POLL_MS = 2000

const formatBytes = (bytes: number): string => {
    const value = Number(bytes) || 0
    if (value < 1024) return `${value} B`
    const units = ['KB', 'MB', 'GB', 'TB']
    let scaled = value / 1024
    let unit = 0
    while (scaled >= 1024 && unit < units.length - 1) {
        scaled /= 1024
        unit++
    }
    return `${scaled.toFixed(scaled >= 10 ? 0 : 1)} ${units[unit]}`
}

const formatTimestamp = (value: number | string | null): string => {
    if (value === null || value === undefined || value === '') return '—'
    const dt =
        typeof value === 'number' || /^\d+$/.test(String(value))
            ? DateTime.fromMillis(Number(value))
            : DateTime.fromISO(String(value))
    return dt.isValid ? dt.toFormat('MMM d, yyyy h:mm:ss a') : '—'
}

const stateBadge = (state: ImageDownscaleState) => {
    switch (state) {
        case 'RUNNING':
            return (
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                    Running
                </Badge>
            )
        case 'DONE':
            return (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    Finished
                </Badge>
            )
        case 'CANCELLED':
            return (
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                    Cancelled
                </Badge>
            )
        case 'FAILED':
            return <Badge variant="destructive">Failed</Badge>
        default:
            return <Badge variant="secondary">Idle</Badge>
    }
}

export const MaintenancePage: React.FC = () => {
    const { hasAdmin } = useSessionStore()
    const isAdmin = hasAdmin()

    const [status, setStatus] = useState<ImageDownscaleStatus | null>(null)
    const [loading, setLoading] = useState(true)
    const [starting, setStarting] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [maxDimension, setMaxDimension] = useState(String(DEFAULT_DIMENSION))

    // Guards the poll timer against firing after unmount.
    const mounted = useRef(true)
    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    const refresh = useCallback(
        async (showSpinner = false) => {
            if (showSpinner) setLoading(true)
            try {
                const next = await MaintenanceEndpoints.getDownscaleImagesStatus()
                if (mounted.current) setStatus(next)
            } catch (error) {
                if (mounted.current) {
                    toast.error(
                        extractErrorMessage(error, 'Failed to load job status')
                    )
                }
            } finally {
                if (mounted.current) setLoading(false)
            }
        },
        []
    )

    useEffect(() => {
        if (!isAdmin) {
            setLoading(false)
            return
        }
        refresh(true)
    }, [isAdmin, refresh])

    // Poll only while the job is actually running — an idle/finished job never changes on its own.
    useEffect(() => {
        if (!isAdmin || status?.state !== 'RUNNING') return
        const timer = window.setInterval(() => refresh(), POLL_MS)
        return () => window.clearInterval(timer)
    }, [isAdmin, status?.state, refresh])

    if (!isAdmin) {
        return (
            <div className="container mx-auto max-w-2xl px-4 py-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Not authorized
                        </CardTitle>
                        <CardDescription>
                            Maintenance jobs are restricted to system
                            administrators.
                        </CardDescription>
                    </CardHeader>
                </Card>
            </div>
        )
    }

    const parsedDimension = parseInt(maxDimension, 10)
    const dimensionError =
        !Number.isFinite(parsedDimension) ||
        parsedDimension < MIN_DIMENSION ||
        parsedDimension > MAX_DIMENSION
            ? `Enter a value between ${MIN_DIMENSION} and ${MAX_DIMENSION}.`
            : ''

    const running = status?.state === 'RUNNING'

    const handleStart = async () => {
        setConfirmOpen(false)
        setStarting(true)
        try {
            const result = await MaintenanceEndpoints.startDownscaleImages(
                parsedDimension
            )
            setStatus(result.status)
            if (result.started) {
                toast.success('Downscale job started.')
            } else {
                toast.info('A downscale job is already running.')
            }
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to start the job'))
        } finally {
            setStarting(false)
        }
    }

    const handleCancel = async () => {
        setCancelling(true)
        try {
            const result = await MaintenanceEndpoints.cancelDownscaleImages()
            setStatus(result.status)
            toast[result.cancelling ? 'success' : 'info'](
                result.cancelling
                    ? 'Cancelling after the current image.'
                    : 'No job is currently running.'
            )
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to cancel the job'))
        } finally {
            setCancelling(false)
        }
    }

    const percent =
        status && status.total ? (status.processed / status.total) * 100 : 0

    return (
        <div className="container mx-auto max-w-3xl px-4 py-6 space-y-6">
            <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                <h1 className="text-xl font-semibold">Maintenance</h1>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ImageDown className="h-5 w-5" />
                        Downscale stored images
                    </CardTitle>
                    <CardDescription>
                        Re-encodes badge logos, back-of-badge images and profile
                        pictures that exceed the size cap now applied at upload
                        time. Runs in the background on the server and can be
                        cancelled between images — leaving this page does not
                        stop it.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                        <div className="space-y-2 sm:w-48">
                            <Label htmlFor="max-dimension">
                                Max dimension (px)
                            </Label>
                            <Input
                                id="max-dimension"
                                type="number"
                                min={MIN_DIMENSION}
                                max={MAX_DIMENSION}
                                value={maxDimension}
                                onChange={(e) => setMaxDimension(e.target.value)}
                                disabled={running || starting}
                            />
                            {dimensionError && (
                                <p className="text-xs text-destructive">
                                    {dimensionError}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={() => setConfirmOpen(true)}
                                disabled={
                                    running || starting || !!dimensionError
                                }
                            >
                                {starting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Starting...
                                    </>
                                ) : (
                                    <>
                                        <Play className="mr-2 h-4 w-4" />
                                        Start job
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                                disabled={!running || cancelling}
                            >
                                {cancelling ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Cancelling...
                                    </>
                                ) : (
                                    <>
                                        <Ban className="mr-2 h-4 w-4" />
                                        Cancel
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => refresh(true)}
                                disabled={loading}
                                aria-label="Refresh status"
                            >
                                <RefreshCw
                                    className={
                                        loading
                                            ? 'mr-2 h-4 w-4 animate-spin'
                                            : 'mr-2 h-4 w-4'
                                    }
                                />
                                Refresh
                            </Button>
                        </div>
                    </div>

                    {loading && !status ? (
                        <div className="flex justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : status ? (
                        <div className="space-y-4 rounded-md border p-4">
                            <div className="flex items-center justify-between gap-3">
                                <Label className="text-sm text-muted-foreground">
                                    Status
                                </Label>
                                {stateBadge(status.state)}
                            </div>

                            <div className="space-y-1">
                                <Progress value={percent} />
                                <p className="text-xs text-muted-foreground">
                                    {status.total === null
                                        ? running
                                            ? 'Counting images...'
                                            : 'No job has been run yet.'
                                        : `${status.processed} of ${status.total} images processed`}
                                </p>
                            </div>

                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                                <div>
                                    <dt className="text-xs text-muted-foreground">
                                        Scaled
                                    </dt>
                                    <dd className="font-medium">
                                        {status.scaled}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">
                                        Skipped (too large)
                                    </dt>
                                    <dd className="font-medium">
                                        {status.skippedTooLarge}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">
                                        Failed
                                    </dt>
                                    <dd className="font-medium">
                                        {status.failed}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">
                                        Space saved
                                    </dt>
                                    <dd className="font-medium">
                                        {formatBytes(status.bytesSaved)}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">
                                        Started
                                    </dt>
                                    <dd className="font-medium">
                                        {formatTimestamp(status.startedAt)}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">
                                        Finished
                                    </dt>
                                    <dd className="font-medium">
                                        {formatTimestamp(status.finishedAt)}
                                    </dd>
                                </div>
                            </dl>

                            {status.error && (
                                <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                    {status.error}
                                </p>
                            )}

                            {status.skippedTooLarge > 0 && (
                                <p className="text-xs text-muted-foreground">
                                    Skipped images are too large to decode
                                    safely and need to be handled manually — see
                                    the server log for their IDs.
                                </p>
                            )}
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            <ConfirmationDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                type="warning"
                title="Start image downscale job?"
                description={`Every stored image larger than ${parsedDimension}px on its longest side will be re-encoded in place. This rewrites the originals and cannot be undone.`}
                confirmText="Start job"
                onConfirm={handleStart}
            />
        </div>
    )
}
